import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import API_BASE from '../../config';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2 } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { WORKFLOW_MODULE_LABELS } from '../../constants/workflowModules';
import { WORKFLOW_ROUTES } from '../../constants/workflowRoutes';

// Kept for backwards-compat (sent to backend); displayed text is richer below
const CONSENT_TEXT = "I understand this is a non-advisory analytical platform presenting historical data only. It does not provide investment or trade recommendations.";
const SESSION_KEY = 'early_access_registered';

function getStoredRegistration() {
  try {
    const s = sessionStorage.getItem(SESSION_KEY);
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
}

function storeRegistration(email, name) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ email: email || '', name: name || '' }));
  } catch {}
}

const MODULE_LABELS = WORKFLOW_MODULE_LABELS;

export default function EarlyAccessModal({ isOpen, onClose, openSource }) {
  const [mode, setMode] = useState('choice'); // 'choice' | 'manual' | 'success' | 'already_registered'
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successEmail, setSuccessEmail] = useState('');
  const [successName, setSuccessName] = useState('');
  const [alreadyOnWaitlist, setAlreadyOnWaitlist] = useState(false);
  const skipAlreadyRegisteredCheck = useRef(false);
  const posthog = usePostHog();

  const reset = () => {
    setMode('choice');
    setEmail('');
    setConsent(false);
    setLoading(false);
    setError('');
    setSuccessEmail('');
    setSuccessName('');
    setAlreadyOnWaitlist(false);
    skipAlreadyRegisteredCheck.current = false;
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // When modal opens, check if user already registered this session
  useEffect(() => {
    if (isOpen && mode === 'choice' && !skipAlreadyRegisteredCheck.current) {
      posthog?.capture('early_access_modal_opened', { source: openSource || 'unknown' });
      const stored = getStoredRegistration();
      if (stored?.email) {
        setMode('already_registered');
        setSuccessEmail(stored.email);
        setSuccessName(stored.name);
      }
    }
  }, [isOpen, mode, posthog, openSource]);

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      setError('Please accept the disclaimer to join the waitlist.');
      return;
    }
    if (!email.trim()) {
      setError('Email is required.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_BASE}/pilot/waitlist`, {
        email: email.trim().toLowerCase(),
        consent: true
      });
      const { email: resEmail, name: resName, already_registered } = res.data;
      setSuccessEmail(resEmail || email.trim().toLowerCase());
      setSuccessName(resName || '');
      setAlreadyOnWaitlist(!!already_registered);
      setMode('success');
      storeRegistration(resEmail || email.trim().toLowerCase(), resName);
      posthog?.capture('waitlist_joined', {
        method: 'email',
        source: openSource || 'unknown',
        already_on_waitlist: !!already_registered,
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    if (!consent) {
      setError('Please accept the disclaimer first.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_BASE}/auth/google`, {
        token: credentialResponse.credential,
        consent: true
      });
      const { pilot_whitelisted, on_waitlist, email: resEmail, name: resName } = res.data;
      if (pilot_whitelisted) {
        posthog?.identify(String(res.data.user_id));
        posthog?.capture('early_access_sign_in_success', {
          method: 'google',
          source: openSource || 'unknown',
        });
        sessionStorage.removeItem('early_access_registered');
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('userId', res.data.user_id);
        localStorage.setItem('userName', res.data.name);
        localStorage.setItem('userPic', res.data.picture);
        handleClose();
        window.location.href = WORKFLOW_ROUTES.setups;
      } else {
        posthog?.capture('waitlist_joined', {
          method: 'google',
          source: openSource || 'unknown',
          already_on_waitlist: !!on_waitlist,
        });
        setSuccessEmail(resEmail || '');
        setSuccessName(resName || '');
        setAlreadyOnWaitlist(!!on_waitlist);
        setMode('success');
        storeRegistration(resEmail, resName);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinWithAnotherEmail = () => {
    skipAlreadyRegisteredCheck.current = true;
    setMode('choice');
    setSuccessEmail('');
    setSuccessName('');
  };

  if (!isOpen) return null;

  const thankYouMessage = () => {
    const who = successName || successEmail || 'for your interest';
    const thanks = successName || successEmail ? `Thanks, ${who}!` : 'Thanks for your interest!';
    const follow = alreadyOnWaitlist
      ? "You're already on the waitlist. We'll reach out soon."
      : "We're onboarding a limited set of users during beta. We'll reach out soon.";
    return `${thanks} ${follow}`;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[20000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-md"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="relative bg-light-paper dark:bg-slate-900 rounded-2xl shadow-2xl shadow-slate-900/20 dark:shadow-black/40 max-w-lg w-full max-h-[90vh] overflow-y-auto scrollbar-sleek"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Gradient accent bar */}
          <div className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl bg-gradient-to-r from-blue-600 via-emerald-500 to-violet-500" />

          <div className="p-8 sm:p-10 pt-10">
            <div className="flex justify-between items-start mb-8">
              <h2 className="text-xl font-semibold tracking-tight bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-300 bg-clip-text text-transparent">
                Early Access
              </h2>
              <button
                onClick={handleClose}
                className="p-2 -m-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                aria-label="Close"
              >
                <X size={18} strokeWidth={2} />
              </button>
            </div>

            <AnimatePresence mode="wait">
              {mode === 'success' ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex gap-4"
                >
                  <div className="shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-50 dark:from-emerald-900/40 dark:to-emerald-800/20 flex items-center justify-center ring-1 ring-emerald-200/50 dark:ring-emerald-700/30">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400" strokeWidth={2.5} />
                  </div>
                  <p className="text-base text-slate-600 dark:text-slate-300 leading-relaxed pt-1">
                    {thankYouMessage()}
                  </p>
                </motion.div>
              ) : mode === 'already_registered' ? (
                <motion.div
                  key="already"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="space-y-5"
                >
                  <p className="text-base text-slate-600 dark:text-slate-300 leading-relaxed">
                    {openSource ? (
                      <>
                        You&apos;re on the waitlist. We&apos;ll reach out to{' '}
                        <span className="font-medium text-slate-900 dark:text-white">{successEmail}</span>
                        {' '}to get you access to{' '}
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-sm font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                          {MODULE_LABELS[openSource] || openSource}
                        </span>
                        {' '}soon.
                      </>
                    ) : (
                      <>
                        You&apos;re on the waitlist. We&apos;ll reach out to{' '}
                        <span className="font-medium text-slate-900 dark:text-white">{successEmail}</span>
                        {' '}soon.
                      </>
                    )}
                  </p>
                  <button
                    onClick={handleJoinWithAnotherEmail}
                    className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                  >
                    Join with another email →
                  </button>
                </motion.div>
              ) : (
                <motion.div
                  key="form"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="space-y-6"
                >
                  {/* Was a hardcoded "Ask AI, Terminal, Scan, and Simulate" —
                      stale even before this pass (Scan is retired from nav,
                      Simulate is being retired per KAN-55) and wrong for
                      every visitor regardless of what they actually clicked.
                      PilotGate always passes a real openSource (it resolves
                      one via workflowAnalyticsSource, never empty) and
                      already uses MODULE_LABELS correctly in the
                      "already_registered" branch just above — this mirrors
                      that. Only the LP's own bare CTAs call
                      openEarlyAccessModal() with no source at all, so THAT
                      case gets the LP's actual two destinations instead of a
                      MODULE_LABELS lookup that would otherwise render
                      "undefined". */}
                  <p className="text-base text-slate-600 dark:text-slate-300 leading-relaxed">
                    Join the waitlist to get early access to{' '}
                    {openSource ? (
                      <span className="font-medium text-slate-800 dark:text-slate-200">
                        {MODULE_LABELS[openSource] || openSource}
                      </span>
                    ) : (
                      <>
                        <span className="font-medium text-slate-800 dark:text-slate-200">Terminal</span>{' '}
                        and{' '}
                        <span className="font-medium text-slate-800 dark:text-slate-200">Playbook</span>
                      </>
                    )}
                    .
                  </p>

                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      className="mt-1 w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-2 focus:ring-blue-500/30 focus:ring-offset-0"
                    />
                    <span className="text-sm text-slate-600 dark:text-slate-300 group-hover:text-slate-700 dark:group-hover:text-slate-200 transition-colors leading-relaxed">
                      I understand this is a non-advisory analytical platform presenting historical data only.
                      It does not provide investment or trade recommendations. I agree to the{' '}
                      <Link
                        to="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="underline underline-offset-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                      >
                        Terms of Use
                      </Link>
                      {' '}and{' '}
                      <Link
                        to="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="underline underline-offset-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                      >
                        Privacy Policy
                      </Link>
                      .
                    </span>
                  </label>

                  {mode === 'choice' ? (
                    <div className="space-y-4">
                      <div
                        className={`w-full transition-opacity ${!consent ? 'opacity-50 pointer-events-none' : ''}`}
                      >
                        <GoogleLogin
                          onSuccess={handleGoogleSuccess}
                          onError={() => setError('Google sign-in failed.')}
                          containerProps={{
                            className:
                              'flex w-full min-h-10 items-center justify-center [&>iframe]:mx-auto',
                          }}
                        />
                      </div>
                      <div className="relative flex items-center gap-4">
                        <div className="flex-grow h-px bg-gradient-to-r from-transparent via-slate-200 dark:via-slate-600 to-transparent" />
                        <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">or</span>
                        <div className="flex-grow h-px bg-gradient-to-r from-transparent via-slate-200 dark:via-slate-600 to-transparent" />
                      </div>
                      <button
                        onClick={() => setMode('manual')}
                        disabled={!consent}
                        className="w-full py-3 px-4 rounded-xl border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50 hover:border-slate-300 dark:hover:border-slate-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all duration-200"
                      >
                        Enter email instead
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleManualSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Email</label>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@example.com"
                          required
                          className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-light-paper dark:bg-slate-800/50 text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                        />
                      </div>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setMode('choice')}
                          className="flex-1 py-3 rounded-xl border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50 font-medium transition-all"
                        >
                          Back
                        </button>
                        <button
                          type="submit"
                          disabled={loading}
                          className="flex-1 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-emerald-500 hover:from-blue-700 hover:to-emerald-600 text-white font-medium shadow-lg shadow-blue-500/25 disabled:opacity-50 disabled:shadow-none transition-all duration-200"
                        >
                          {loading ? 'Submitting...' : 'Join waitlist'}
                        </button>
                      </div>
                    </form>
                  )}

                  {error && (
                    <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
